import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, Kanban, Users, MessagesSquare, CalendarRange, Megaphone, FileSignature, Wallet, Building2, Bell, LogOut, Compass, ScrollText, Boxes, Heart, Newspaper, Vault, Handshake, ListChecks, Mail, UsersRound, Activity, Sparkles } from "lucide-react";
import { Wordmark, Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ITEMS = [
  ["/app", "Overview", LayoutGrid, true],
  ["/app/pipeline", "Pipeline", Kanban],
  ["/app/deal-rooms", "Deal rooms", Handshake],
  ["/app/approvals", "Approvals", ListChecks],
  ["/app/network", "Network", Users],
  ["/app/briefs", "Briefs", ScrollText],
  ["/app/inventory", "Inventory", Boxes],
  ["/app/wishlist", "Wishlist", Heart],
  ["/app/messages", "Messages", MessagesSquare],
  ["/app/templates", "Templates", Mail],
  ["/app/calendar", "Calendar", CalendarRange],
  ["/app/events", "Events", Megaphone],
  ["/app/agreements", "Agreements", FileSignature],
  ["/app/dataroom", "Data room", Vault],
  ["/app/payments", "Payments", Wallet],
  ["/app/promote", "Promote", Sparkles],
  ["/app/profile", "Profile", Building2],
  ["/app/team", "Team", UsersRound],
  ["/app/newsroom", "Newsroom", Newspaper, false, true],
  ["/app/hq", "HQ", Activity, false, true],
];

function useUnread() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => api.get("/alerts").then((r) => setN(r.data?.unread ?? 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  return n;
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#E0218A]/12 text-[#2A1240] border border-[#E0218A]/30" : "text-slate-300 hover:text-[#2A1240] hover:bg-[#2A1240]/5 border border-transparent"}`;

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const unread = useUnread();
  const items = ITEMS.filter((i) => !i[4] || user?.role === "admin");
  const out = async () => {
    await logout();
    nav("/");
  };
  return (
    <div className="min-h-screen bg-[#FFF7FB] text-[#2A1240]" data-testid="app-shell">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-[#2A1240]/10 bg-[#FFF0F7] p-5">
        <Link to="/" data-testid="app-home-link"><Wordmark /></Link>
        <nav className="mt-10 flex-1 space-y-1 overflow-y-auto no-scrollbar" data-lenis-prevent>
          {items.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} className={linkCls} data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon className="h-4 w-4" />{label}
            </NavLink>
          ))}
          <Link to="/discover" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-300 hover:text-[#2A1240]" data-testid="sidebar-discover"><Compass className="h-4 w-4" />Discover</Link>
        </nav>
        <div className="border-t border-[#2A1240]/10 pt-4">
          <p className="text-sm text-[#2A1240] truncate" data-testid="app-user-name">{user?.name}</p>
          <p className="text-xs text-slate-400 truncate">{user?.company?.name} · {user?.email}</p>
          <button onClick={out} className="mt-3 flex items-center gap-2 text-xs text-slate-300 hover:text-[#2A1240]" data-testid="app-logout-button"><LogOut className="h-3.5 w-3.5" />Sign out</button>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#FFF7FB]/85 border-b border-[#2A1240]/10 h-16 px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" className="lg:hidden"><Logo /></Link>
          <p className="hidden lg:block text-sm text-slate-400">{user?.company?.name}</p>
          <div className="flex items-center gap-3">
            <Link to="/app/alerts" className="relative grid h-10 w-10 place-items-center rounded-full border border-[#2A1240]/15 hover:border-[#E0218A]" data-testid="alerts-bell">
              <Bell className="h-4 w-4" />
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#E0218A] text-white text-[11px] font-bold grid place-items-center" data-testid="alerts-unread-count">{unread}</span>}
            </Link>
            <button onClick={out} className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-[#2A1240]/15" data-testid="app-mobile-logout" aria-label="Sign out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="px-5 sm:px-8 py-8 pb-28 lg:pb-12 max-w-[1400px]"><Outlet /></main>
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#2A1240]/10 bg-[#FFF0F7]/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tabbar">
        <div className="flex overflow-x-auto no-scrollbar">
          {items.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`tab-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex min-w-[72px] flex-col items-center gap-1 px-3 py-2.5 text-[11px] ${isActive ? "text-[#8E2BC4]" : "text-slate-300"}`}>
              <Icon className="h-5 w-5" />{label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export const PageHead = ({ kicker, title, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div>
      {kicker && <p className="kicker">{kicker}</p>}
      <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#2A1240]">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Card = ({ className = "", children, ...p }) => (
  <div className={`rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] ${className}`} {...p}>{children}</div>
);
